<template>
  <div class="min-h-screen bg-white">
    <div class="max-w-screen-xl mx-auto px-6 py-10 lg:py-14">

      <!-- Breadcrumb -->
      <nav class="flex items-center gap-2 text-[0.82rem] text-[#999] mb-6" aria-label="Kruimelpad">
        <router-link to="/" class="hover:text-[#111] transition-colors no-underline"><i class="bi bi-house"></i></router-link>
        <i class="bi bi-chevron-right text-[0.7rem]"></i>
        <span class="text-[#111] font-medium">{{ title }}</span>
      </nav>

      <div class="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 lg:gap-14">

        <!-- Zijbalk-inhoudsopgave (desktop) -->
        <aside class="hidden lg:block">
          <nav v-if="toc.length" class="sticky top-24" aria-label="Op deze pagina">
            <p class="text-[0.72rem] font-bold uppercase tracking-[0.1em] text-[#aaa] mb-3">Op deze pagina</p>
            <ul class="list-none p-0 m-0 border-l border-[#eee]">
              <li v-for="item in toc" :key="item.id">
                <a :href="`#${item.id}`" @click.prevent="scrollTo(item.id)"
                   class="block pl-4 -ml-px border-l-2 border-transparent py-1 text-[0.85rem] text-[#777] no-underline hover:text-[#111] hover:border-[#FFD500] transition-colors"
                   :class="{ 'toc-active': activeId === item.id }">
                  {{ item.text }}
                </a>
              </li>
            </ul>
          </nav>
        </aside>

        <!-- Content -->
        <article class="max-w-[760px]">
          <h1 class="text-[2rem] lg:text-[2.25rem] font-extrabold text-[#111] mb-2 leading-tight">{{ title }}</h1>
          <p v-if="updated" class="text-[0.82rem] text-[#aaa] mb-6">{{ updated }}</p>

          <!-- Mobiele inhoudsopgave: uitklapbaar (geen zijbalk op mobiel) -->
          <details v-if="toc.length" class="lg:hidden mb-7 border border-[#eee] rounded-xl bg-[#fafafa]">
            <summary class="cursor-pointer select-none px-4 py-3 text-[0.85rem] font-semibold text-[#333] flex items-center justify-between">
              Op deze pagina
              <i class="bi bi-chevron-down text-[0.75rem] chevron"></i>
            </summary>
            <ul class="list-none p-0 m-0 px-4 pb-3 space-y-1.5">
              <li v-for="item in toc" :key="item.id">
                <a :href="`#${item.id}`" @click.prevent="scrollTo(item.id)"
                   class="text-[0.85rem] text-[#666] no-underline hover:text-[#111]">{{ item.text }}</a>
              </li>
            </ul>
          </details>

          <div ref="contentRef" class="prose-content">
            <slot />
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

defineProps({
  title:   { type: String, required: true },
  updated: { type: String, default: '' },
})

const contentRef = ref(null)
const toc        = ref([])       // gevuld client-side (na mount) → geen SSR-hydration-mismatch
const activeId   = ref('')
let observer = null

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')  // accenten weg
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60) || 'sectie'
}

function scrollTo(id) {
  const el = document.getElementById(id)
  if (!el) return
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' })
  history.replaceState(null, '', `#${id}`)
}

onMounted(() => {
  if (!contentRef.value) return
  const headings = [...contentRef.value.querySelectorAll('h2')]
  const seen = {}
  toc.value = headings.map(h => {
    let id = slugify(h.textContent)
    if (seen[id]) id = `${id}-${seen[id]++}`; else seen[id] = 1
    h.id = id
    h.style.scrollMarginTop = '90px'
    return { id, text: h.textContent.trim() }
  })

  // Actief kopje markeren tijdens scrollen
  if ('IntersectionObserver' in window && headings.length) {
    observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) activeId.value = e.target.id }),
      { rootMargin: '-80px 0px -70% 0px' }
    )
    headings.forEach(h => observer.observe(h))
  }
})

onBeforeUnmount(() => observer && observer.disconnect())
</script>

<style scoped>
.toc-active { color: #111 !important; border-color: #FFD500 !important; font-weight: 500; }
.chevron { transition: transform 0.2s ease; }
details[open] > summary .chevron { transform: rotate(180deg); }

/* Gedeelde typografie voor álle tekstpagina's (slot-content) */
.prose-content :deep(h2) { font-size: 1.25rem; font-weight: 700; color: #111; margin-top: 2rem; margin-bottom: 0.75rem; }
.prose-content :deep(h2:first-child) { margin-top: 0; }
.prose-content :deep(h3) { font-size: 1.05rem; font-weight: 600; color: #111; margin-top: 1.5rem; margin-bottom: 0.5rem; }
.prose-content :deep(p)  { font-size: 1rem; color: #444; line-height: 1.75; margin-bottom: 1rem; }
.prose-content :deep(ul), .prose-content :deep(ol) { margin: 0 0 1rem 1.25rem; color: #444; line-height: 1.75; }
.prose-content :deep(li) { margin-bottom: 0.4rem; }
.prose-content :deep(a)  { color: #b8860b; text-decoration: underline; }
.prose-content :deep(a:hover) { color: #111; }
</style>
